/**
 * The HTTP errors `SharedHttpInterceptor` has already raised a snack for.
 *
 * A caller that announces its own failure (WCAG 4.1.3) must not say it a second time when the
 * interceptor's snack -- which announces itself -- already did. Callers used to answer that by
 * re-deriving the interceptor's branch from the status code, and the copy was wrong the moment the
 * branch was not the one they mirrored: a request that 401s, refreshes and then fails on the RETRY
 * never went through the generic snack, while the copy said it had (MR !197 review, round 15). So
 * the interceptor records what it actually did, on the error instance it rethrows, and callers read
 * that instead of guessing.
 *
 * A WeakSet, so an error nobody holds any more is not kept alive by having been toasted.
 */
const toasted = new WeakSet<object>();

/** Record that a snack has been raised for `error`. Called by the interceptor only. */
export function markErrorToasted(error: unknown): void {
  if (error !== null && typeof error === 'object') {
    toasted.add(error);
  }
}

/** Whether the interceptor raised a snack for this exact error instance. */
export function errorWasToasted(error: unknown): boolean {
  return error !== null && typeof error === 'object' && toasted.has(error);
}

/**
 * The HTTP errors whose request ended the session (or handed the tab to another user). The teardown
 * navigates to the login page -- or the identity switch raises its own notice -- and THAT is the
 * explanation the user gets: a caller announcing its own failure on top ("Not added to the cart")
 * gives a misleading reason for what happened (MR !197 review, round 16).
 */
const sessionEnding = new WeakSet<object>();

/** Record that `error`'s request ended the session. Called by the interceptor only. */
export function markErrorEndedSession(error: unknown): void {
  if (error !== null && typeof error === 'object') {
    sessionEnding.add(error);
  }
}

/**
 * Whether the user has already been told what happened to this request: the interceptor raised a
 * snack for it, or its 401 ended the session. A caller announcing its own failure asks this.
 */
export function errorAlreadyExplained(error: unknown): boolean {
  return errorWasToasted(error) || (error !== null && typeof error === 'object' && sessionEnding.has(error));
}
